import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";
import { SchoolAdminsCard } from "./school-admins-card";
import { SCHOOL_LIST_QUERY_KEY } from "./school-list";
import { SchoolPaymentConfig } from "./school-payment-config";
import { SchoolBunnyConfig } from "./school-bunny-config";
import { SchoolEmailAccountCard } from "./school-email-account-card";
import { SchoolSettingsCard } from "./school-settings-card";
import { SchoolInviteStatus, type SchoolInvite } from "./school-invite-status";
import { SchoolRenameForm } from "./school-rename-form";
import { SchoolSetupChecklist, type SchoolSetupStep } from "./school-setup-checklist";

/** The FR-8 wire shape (`GET /api/platform/admin/tenants/:tenantId`, F15). */
export type SchoolDetailData = {
  tenant: { id: string; slug: string; name: string; createdAt: string; logoUrl: string | null };
  setup: SchoolSetupStep[];
  invite: SchoolInvite;
  aiTutorEnabled: boolean;
  emailAccountNotice: boolean;
};

export const schoolDetailQueryKey = (tenantId: string) => ["platform-school", tenantId] as const;

async function fetchSchool(tenantId: string): Promise<SchoolDetailData> {
  const res = await platformFetch(`/platform/admin/tenants/${tenantId}`);
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: SchoolDetailData }).data;
}

/**
 * MT-1 Plan 2 S14 (FR-8, FR-8.10, FR-36, FR-3 c/d UI): one school's page — the checklist, the
 * FR-8.10 notice, the AI tutor state, the first admin's invite and the rename form. Slots (in
 * this order) for Plan 3's `SchoolEmailAccountCard`, Plan 4's `SchoolBunnyConfig` and TEN-006's
 * settings card: each plan inserts its own component below the invite.
 */
export function SchoolDetail({ tenantId }: { tenantId: string }) {
  const t = useTranslations("platform.school");
  const tRoot = useTranslations();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: schoolDetailQueryKey(tenantId),
    queryFn: () => fetchSchool(tenantId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: schoolDetailQueryKey(tenantId) });
    void queryClient.invalidateQueries({ queryKey: SCHOOL_LIST_QUERY_KEY });
  };

  return (
    <div data-testid="school-detail">
      <Link to="/platform/admin" className="text-sm underline">
        {t("back")}
      </Link>
      {query.isPending && <p className="mt-4 text-sm text-[var(--text-muted)]">{t("loading")}</p>}
      {query.isError && (
        <p role="alert" className="mt-4 text-sm text-[var(--danger)]">
          {apiErrorText(query.error, tRoot, "platform.school.loadFailed", {
            namespace: "platform.errors",
          })}
        </p>
      )}
      {query.data && (
        <>
          <h1 className="mt-4 text-2xl font-semibold" data-testid="school-name">
            {query.data.tenant.name}
          </h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {t("address", { slug: query.data.tenant.slug })}
          </p>
          {query.data.emailAccountNotice && (
            <p
              role="status"
              className="mt-4 rounded-md border border-[var(--border-strong)] p-3 text-sm"
              data-testid="school-email-notice"
            >
              {t("notice")}
            </p>
          )}
          <SchoolSetupChecklist steps={query.data.setup} />
          <p className="mt-4 text-sm" data-testid="school-ai-tutor">
            {t("aiTutor", { state: query.data.aiTutorEnabled ? t("aiTutorOn") : t("aiTutorOff") })}
          </p>
          <SchoolInviteStatus tenantId={tenantId} invite={query.data.invite} onResent={refresh} />
          <SchoolAdminsCard tenantId={tenantId} onChanged={refresh} />
          <SchoolPaymentConfig tenantId={tenantId} />
          <SchoolEmailAccountCard tenantId={tenantId} schoolName={query.data.tenant.name} />
          <SchoolBunnyConfig tenantId={tenantId} />
          <SchoolSettingsCard tenantId={tenantId} schoolName={query.data.tenant.name} />
          <SchoolRenameForm
            key={tenantId}
            tenantId={tenantId}
            currentName={query.data.tenant.name}
            onRenamed={refresh}
          />
        </>
      )}
    </div>
  );
}
